import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  hrmsGetRequest,
  hrmsPostRequest,
  hrmsUpdateRequest,
} from "../services";
import toast from "react-hot-toast";

const initialState = {
  goalPlannings: [],
  activeGoalPlanning: null,
  pendingReview: [],
  currentGoalPlanning: null,
  loading: false,
  error: null,
};

export const initiateGoalPlanning = createAsyncThunk(
  "goalPlanning/initiate",
  async ({ adminEmail, adminName, initiatedByEmail, initiatedByUserId }, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequest("/api/goal-planning/initiate", {
        adminEmail,
        adminName,
        initiatedByEmail,
        initiatedByUserId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message || error.message || "Failed to initiate goal planning"
      );
    }
  }
);

export const fetchGoalPlanningsByEmail = createAsyncThunk(
  "goalPlanning/fetchByEmail",
  async (adminEmail, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(
        `/api/goal-planning/email/${encodeURIComponent(adminEmail)}`
      );
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchActiveGoalPlanning = createAsyncThunk(
  "goalPlanning/fetchActive",
  async (adminEmail, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest(
        `/api/goal-planning/active/${encodeURIComponent(adminEmail)}`
      );
      return response.data || null;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPendingReviewGoalPlannings = createAsyncThunk(
  "goalPlanning/fetchPendingReview",
  async (_, { rejectWithValue }) => {
    try {
      const response = await hrmsGetRequest("/api/goal-planning/pending-review");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const saveAdminDraftGoalPlanning = createAsyncThunk(
  "goalPlanning/adminDraft",
  async ({ id, adminEmail, formData }, { rejectWithValue }) => {
    try {
      const payload = {
        adminEmail,
        pselOpportunities: formData.pselOpportunities,
        leadershipQuestions: formData.leadershipQuestions,
        professionalExperiences: formData.professionalExperiences,
        newLearning: formData.newLearning,
        goalsAndRationale: formData.goalsAndRationale,
        learningPlan: formData.learningPlan,
        learningActionQuestions: formData.learningActionQuestions,
        evidenceOfLearning: formData.evidenceOfLearning,
        multiYearFocus: formData.multiYearFocus,
        schoolDistrictContribution: formData.schoolDistrictContribution,
      };

      const response = await hrmsUpdateRequest(
        `/api/goal-planning/${id}/admin-draft`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message ||
          error.message ||
          "Failed to save goal planning draft"
      );
    }
  }
);

export const submitAdminGoalPlanning = createAsyncThunk(
  "goalPlanning/adminSubmit",
  async ({ id, adminEmail, formData }, { rejectWithValue }) => {
    try {
      const payload = {
        adminEmail,
        pselOpportunities: formData.pselOpportunities,
        leadershipQuestions: formData.leadershipQuestions,
        professionalExperiences: formData.professionalExperiences,
        newLearning: formData.newLearning,
        goalsAndRationale: formData.goalsAndRationale,
        learningPlan: formData.learningPlan,
        learningActionQuestions: formData.learningActionQuestions,
        evidenceOfLearning: formData.evidenceOfLearning,
        multiYearFocus: formData.multiYearFocus,
        schoolDistrictContribution: formData.schoolDistrictContribution,
      };

      const response = await hrmsUpdateRequest(
        `/api/goal-planning/${id}/admin-submit`,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message || error.message || "Failed to submit goal planning"
      );
    }
  }
);

export const submitConferenceGoalPlanning = createAsyncThunk(
  "goalPlanning/conferenceSubmit",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `/api/goal-planning/${id}/conference`,
        formData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message ||
          error.message ||
          "Failed to submit goal setting conference"
      );
    }
  }
);

export const reinitiateGoalPlanning = createAsyncThunk(
  "goalPlanning/reinitiate",
  async (id, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(
        `/api/goal-planning/${id}/reinitiate`,
        {}
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error?.data?.message || error.message || "Failed to reinitiate goal planning"
      );
    }
  }
);

const goalPlanningSlice = createSlice({
  name: "goalPlanning",
  initialState,
  reducers: {
    clearCurrentGoalPlanning: (state) => {
      state.currentGoalPlanning = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initiateGoalPlanning.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initiateGoalPlanning.fulfilled, (state, action) => {
        state.loading = false;
        state.currentGoalPlanning = action.payload;
        state.activeGoalPlanning = action.payload;
        if (action.payload?.id) {
          const existingIndex = state.goalPlannings.findIndex(
            (gp) => gp.id === action.payload.id
          );
          if (existingIndex !== -1) {
            state.goalPlannings[existingIndex] = action.payload;
          } else {
            state.goalPlannings = [action.payload, ...state.goalPlannings];
          }
        }
        toast.success("Goal planning initiated successfully");
      })
      .addCase(initiateGoalPlanning.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        toast.error(action.payload);
      })
      .addCase(fetchGoalPlanningsByEmail.fulfilled, (state, action) => {
        state.goalPlannings = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchActiveGoalPlanning.fulfilled, (state, action) => {
        state.activeGoalPlanning = action.payload;
      })
      .addCase(fetchPendingReviewGoalPlannings.fulfilled, (state, action) => {
        state.pendingReview = action.payload;
      })
      .addCase(saveAdminDraftGoalPlanning.fulfilled, (state, action) => {
        state.activeGoalPlanning = action.payload;
        state.currentGoalPlanning = action.payload;
        const existingIndex = state.goalPlannings.findIndex(
          (gp) => gp.id === action.payload.id
        );
        if (existingIndex !== -1) {
          state.goalPlannings[existingIndex] = action.payload;
        } else {
          state.goalPlannings = [action.payload, ...state.goalPlannings];
        }
        toast.success("Progress saved. You can continue later.");
      })
      .addCase(saveAdminDraftGoalPlanning.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(submitAdminGoalPlanning.fulfilled, (state, action) => {
        state.activeGoalPlanning = null;
        state.currentGoalPlanning = action.payload;
        const existingIndex = state.goalPlannings.findIndex(
          (gp) => gp.id === action.payload.id
        );
        if (existingIndex !== -1) {
          state.goalPlannings[existingIndex] = action.payload;
        } else {
          state.goalPlannings = [action.payload, ...state.goalPlannings];
        }
        toast.success("Goal planning submitted successfully");
      })
      .addCase(submitAdminGoalPlanning.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(submitConferenceGoalPlanning.fulfilled, (state, action) => {
        state.currentGoalPlanning = action.payload;
        toast.success("Goal setting conference completed");
      })
      .addCase(submitConferenceGoalPlanning.rejected, (state, action) => {
        toast.error(action.payload);
      })
      .addCase(reinitiateGoalPlanning.fulfilled, (state, action) => {
        state.activeGoalPlanning = action.payload;
        state.currentGoalPlanning = action.payload;
        const existingIndex = state.goalPlannings.findIndex(
          (gp) => gp.id === action.payload.id
        );
        if (existingIndex !== -1) {
          state.goalPlannings[existingIndex] = action.payload;
        } else {
          state.goalPlannings = [action.payload, ...state.goalPlannings];
        }
        toast.success(
          "Form reinitiated and sent back to admin for updates"
        );
      })
      .addCase(reinitiateGoalPlanning.rejected, (state, action) => {
        toast.error(action.payload);
      });
  },
});

export const { clearCurrentGoalPlanning } = goalPlanningSlice.actions;
export default goalPlanningSlice.reducer;
